import * as chroma from 'chroma-js';

interface ColorPalette {
  50: string;
  100: string;
  200: string;
  300: string;
  400: string;
  500: string;
  600: string;
  700: string;
  800: string;
  900: string;
  contrast?: ColorPalette;
}

export interface KeepsTheme {
  primary: ColorPalette;
  accent: ColorPalette;
  warn: ColorPalette;
}

export function generateTheme(primaryColor: string): KeepsTheme {
  const hexRegex = /[0-9A-Fa-f]{6}/g;

  if (!hexRegex.test(primaryColor)) {
    primaryColor = '#673ab7';
  }

  const primary = generatePalette(primaryColor);
  const warn = generatePalette('#f44336');
  const accent = primary;

  return { primary, accent, warn };
}

export function generatePalette(baseColor: string): ColorPalette {
  const palette: ColorPalette = {
    50: null,
    100: null,
    200: null,
    300: null,
    400: null,
    500: baseColor,
    600: null,
    700: null,
    800: null,
    900: null,
  };

  // Prepare the colors array
  const colors = Object.values(palette).filter((color) => color);

  // Generate a very dark and a very light versions of the
  // default color to use them as the boundary colors rather
  // than using pure white and pure black. This will stop
  // in between colors' hue values to slipping into the grays.
  colors.unshift(chroma.scale(['white', palette[500]]).domain([0, 1]).mode('lrgb').colors(50)[1]);
  colors.push(chroma.scale(['black', palette[500]]).domain([0, 1]).mode('lrgb').colors(10)[1]);

  // Prepare the domains array
  const domain = [0, 0.5, 1];

  // Generate the color scale
  const scale = chroma.scale(colors).domain(domain).mode('lrgb');

  const colorPalette: ColorPalette = {
    50: scale(0.05).hex(),
    100: scale(0.1).hex(),
    200: scale(0.2).hex(),
    300: scale(0.3).hex(),
    400: scale(0.4).hex(),
    500: scale(0.5).hex(),
    600: scale(0.6).hex(),
    700: scale(0.7).hex(),
    800: scale(0.8).hex(),
    900: scale(0.9).hex(),
  };

  const contrasts = generateContrast(colorPalette);

  return { ...colorPalette, contrast: contrasts };
}

function generateContrast(palette: ColorPalette): any {
  const lightColor = '#FFFFFF';
  let darkColor = '#FFFFFF';

  // Iterate through the palette to find the darkest color
  Object.values(palette).forEach((color) => {
    darkColor = chroma.contrast(color, '#FFFFFF') > chroma.contrast(darkColor, '#FFFFFF') ? color : darkColor;
  });

  const paletteWithContrast = Object.entries(palette).map(([hue, color]) => {
    const contrastColor =
      chroma.contrast(color, darkColor) > chroma.contrast(color, lightColor) ? darkColor : lightColor;
    return [hue, contrastColor];
  });

  return Object.fromEntries(paletteWithContrast);
}
